import { useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, Check, Loader2, ShieldCheck, X } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { EmptyState } from "~/components/ui/empty-state";
import { Textarea } from "~/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/hooks/use-toast";
import { useProfileStore } from "~/store/profileStore";
import {
  InvPendingPicking,
  inventoryOperationsService,
} from "~/services/inventory/operationsService";
import { formatDate } from "~/utils/helpers";

const formatQty = (n: number | null) =>
  n == null ? "-" : new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n);

export function InventoryApprovalsPage() {
  const queryClient = useQueryClient();
  const userId = useProfileStore((s) => s.id);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<InvPendingPicking | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["inv-pending"],
    queryFn: () => inventoryOperationsService.getPending(),
  });
  const rows = data ?? [];

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ["inv-pending"] });
    queryClient.invalidateQueries({ queryKey: ["inv-stock"] });
    queryClient.invalidateQueries({ queryKey: ["inv-movements"] });
  };

  const approve = async (p: InvPendingPicking) => {
    setProcessingId(p.picking_id);
    try {
      await inventoryOperationsService.approve(p.picking_id, userId);
      toast({ title: "Documento aprobado", description: p.name });
      refetch();
    } catch (e) {
      toast({
        title: "No se pudo aprobar",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setProcessingId(rejectTarget.picking_id);
    try {
      await inventoryOperationsService.reject(rejectTarget.picking_id, userId, rejectReason);
      toast({ title: "Documento rechazado", description: rejectTarget.name });
      setRejectTarget(null);
      setRejectReason("");
      refetch();
    } catch (e) {
      toast({
        title: "No se pudo rechazar",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <PageContainer>
      <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2.5 text-sm text-muted-foreground">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <span>
          Módulo de inventario <strong className="font-semibold">nuevo — en pruebas</strong>. Quien
          aprueba debe ser distinto de quien registró (segregación de funciones).
        </span>
      </div>

      <PageHeader
        title="Inventario — Aprobaciones"
        description="Documentos en borrador que esperan autorización para mover stock."
      />

      {isLoading ? (
        <Card>
          <CardContent className="space-y-3 p-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="Sin pendientes"
          description="No hay documentos esperando aprobación."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Documento</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Movimiento</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead>Registrado por</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((p) => (
                    <TableRow key={p.picking_id}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {p.name}
                      </TableCell>
                      <TableCell>{p.tipo}</TableCell>
                      <TableCell className="font-medium">
                        {p.code ? <span className="text-muted-foreground">{p.code} · </span> : null}
                        {p.producto ?? "-"}
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 whitespace-nowrap text-sm text-muted-foreground">
                          {p.origen ?? "-"}
                          <ArrowRight className="h-3 w-3" />
                          {p.destino ?? "-"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQty(p.cantidad)} {p.uom ?? ""}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {p.created_by_name ?? "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDate(p.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800"
                            disabled={processingId === p.picking_id}
                            onClick={() => approve(p)}
                          >
                            {processingId === p.picking_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                            <span className="ml-1">Aprobar</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700"
                            disabled={processingId === p.picking_id}
                            onClick={() => setRejectTarget(p)}
                          >
                            <X className="h-4 w-4" />
                            <span className="ml-1">Rechazar</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rechazar {rejectTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Motivo del rechazo (opcional)"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-9" onClick={() => setRejectTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              className="h-9"
              disabled={!!processingId}
              onClick={confirmReject}
            >
              {processingId ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

export default InventoryApprovalsPage;
